<script lang="ts" setup>
import userStore from '@/store/UserStore'
import projectStore from '@/store/ProjectStore'
import formStore from '@/store/FormStore'
import { computed, nextTick, ref, watch } from 'vue'
import { StepsController } from '@/controllers/StepsController'
import { formatNumber } from '@/helper/Translations'
import { Buffertank } from '@/services/models/implementations/Buffertank'
import ProductsSelectTable, { ProductColumn } from '@/components/ProductsSelectTable.vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ButtonSet from '@/components/ButtonSet.vue'
import { validateComponent, ValidatorType } from '@/helper/ValidateDirective'
import ExclusionHint from '@/components/steps/components/ExclusionHint.vue'
import ProductAvailabilityLink from '@/components/ProductAvailabilityLink.vue'
import { InstallationType, RecommendationBadgeType } from '@/helper/Types'
import HintBox from '@/components/HintBox.vue'
import HelpLabel from '@/components/HelpLabel.vue'
import { ModalTypes } from '@/helper/ModalTypes.ts'
import { ProductResponse } from '@/services/models/ProductResponse.ts'

const props = defineProps<{
  validator: ValidatorType
}>()

const bufferTankIds = [formStore.data.coolingBufferTankId, formStore.data.bufferTankId].filter(b => !!b)
const singleBufferTank = ref<boolean>(bufferTankIds.length <= 1)

const heatingBufferTanks = computed<Buffertank[]>(() => projectStore.bufferTankSuggestion?.heating || [])
const coolingBufferTanks = computed<Buffertank[]>(() => projectStore.bufferTankSuggestion?.cooling || [])
const combinedBufferTanks = computed<Buffertank[]>(() => projectStore.bufferTankSuggestion?.combined || [])

const minBufferVolumeHeating = computed<number>(() => Math.round(projectStore.bufferTankSuggestion?.minVolume?.heatingEffective || 0))
const minBufferVolumeCooling = computed<number>(() => Math.round(projectStore.bufferTankSuggestion?.minVolume?.coolingEffective || 0))
const minBufferVolumeCombined = computed<number>(() => Math.round(projectStore.bufferTankSuggestion?.minVolume?.combined || 0))

const minFlowRate = computed<number>(() => projectStore.bufferTankSuggestion?.minFlowRate || 0)
const minBufferVolume = computed(() => Math.max(minBufferVolumeCombined.value, minBufferVolumeHeating.value, minBufferVolumeCooling.value))
const isSalePackage = computed(() => projectStore.projectProperties.salePackageId.val)
const bufferTankSalePackage = ref<boolean>(false)

const bufferTankIncluded = computed({
  get() {
    return !formStore.data.explicitlyNoBufferTankSelected
  },
  set(val: boolean) {
    formStore.data.explicitlyNoBufferTankSelected = !val
  }
})

const bufferTankColumns: ProductColumn[] = [
  {
    columnName: 'step_components.table_product',
    cssClass: 'is--product',
    valCb: (val: ProductResponse) => val.name
  },
  {
    columnName: 'step_components.table_storage_volume',
    cssClass: 'is--volume',
    valCb: (val: ProductResponse) => `${formatNumber((val as Buffertank).chr?.volume)} l`
  }
]

if (userStore.enableBufferMinVolumeHint) {
  bufferTankColumns.push(
    {
      columnName: 'step_components.table_max_flow_rate',
      cssClass: 'is--flowrate',
      valCb: (val: ProductResponse) => `${formatNumber((val as Buffertank).chr?.maxFlowRate)} m³/h`
    }
  )
}

function uncoveredVolume(installationType?: InstallationType): number {
  const heatingBufferTank = heatingBufferTanks.value.find(b => b.id === formStore.data.bufferTankId)
  const coolingBufferTank = coolingBufferTanks.value.find(b => b.id === formStore.data.coolingBufferTankId)

  if (singleBufferTank.value) {
    return minBufferVolume.value - (heatingBufferTank?.chr.volume || coolingBufferTank?.chr.volume || 0)
  }

  if (installationType === InstallationType.HEATING) {
    return minBufferVolumeHeating.value - (heatingBufferTank?.chr.volume || 0)
  } else {
    return minBufferVolumeCooling.value - (coolingBufferTank?.chr.volume || 0)
  }
}

function uncoveredVolumePercentage(minBufferVolume: number, uncoveredBufferVolume: number): number {
  return Math.round((minBufferVolume - uncoveredBufferVolume) / minBufferVolume * 100)
}

function bufferTankIncludedChanged(e: Event): void {
  if (!(e.currentTarget as HTMLInputElement).checked) {
    formStore.data.bufferTankId = ''
    formStore.data.coolingBufferTankId = ''
  }

  StepsController.currentStepValid(false)
}

function individualTankSelectionChanged(): void {
  formStore.data.bufferTankId = ''
  formStore.data.coolingBufferTankId = ''

  StepsController.currentStepValid(false)
}

function validate(): boolean {
  let bufferTankSelected = false

  if (singleBufferTank.value) {
    if (!formStore.data.explicitlyNoBufferTankSelected && (!heatingBufferTanks.value?.length && !coolingBufferTanks.value?.length && !combinedBufferTanks.value?.length)) {
      return false
    }

    bufferTankSelected = !!formStore.data.bufferTankId || !!formStore.data.coolingBufferTankId
  } else {
    bufferTankSelected = !!formStore.data.bufferTankId && !!formStore.data.coolingBufferTankId

    if (!projectStore.isCooling) {
      bufferTankSelected = !!formStore.data.bufferTankId
    } else if (!projectStore.isHeating) {
      bufferTankSelected = !!formStore.data.coolingBufferTankId
    }
  }

  return (!!formStore.data.explicitlyNoBufferTankSelected || bufferTankSelected)
    && validateComponent(props.validator)
}

watch(
  [
    () => coolingBufferTanks.value,
    () => combinedBufferTanks.value,
    () => heatingBufferTanks.value,
  ],
  () => {
    const { coolingBufferTankId, bufferTankId } = formStore.data

    if (coolingBufferTankId || bufferTankId) {

      if (singleBufferTank.value) {
        const single = projectStore.isCooling
          ? combinedBufferTanks.value.find(t => t.id === bufferTankId)
          : heatingBufferTanks.value.find(t => t.id === bufferTankId)

        if (!single) {
          formStore.data.bufferTankId = ''
        }
        return
      }

      const individualCooling = coolingBufferTanks.value.find(t => t.id === coolingBufferTankId)
      const individualHeating = heatingBufferTanks.value.find(t => t.id === bufferTankId)

      if (!individualCooling) {
        formStore.data.coolingBufferTankId = ''
      }

      if (!individualHeating) {
        formStore.data.bufferTankId = ''
      }
    }
  },
  { immediate: true }
)

watch(
  [
    () => singleBufferTank.value,
    () => heatingBufferTanks.value,
    () => coolingBufferTanks.value,
    () => combinedBufferTanks.value,
    () => isSalePackage.value,
    () => projectStore.isCooling,
    () => bufferTankSalePackage.value
  ],
  async ([single, heating, cooling, combined, isSalePackage, isCooling]) => {
    await nextTick()

    if (isSalePackage) {

      const prevBufferTankId = formStore.data.bufferTankId
      formStore.data.bufferTankId = projectStore.projectProperties.bufferTankId.val
      formStore.data.coolingBufferTankId = projectStore.projectProperties.coolingBufferTankId.val

      if (single) {

        const selected = isCooling
          ? combined.find(t => t.inSalePackage)
          : heating.find(t => t.inSalePackage)

        if (selected) {
          formStore.data.bufferTankId = selected.id
          bufferTankSalePackage.value = true

          await StepsController.currentStepValid(true)
        }
      } else {
        const selected = cooling.find(t => t.inSalePackage)
        if (selected) {
          formStore.data.coolingBufferTankId = selected.id
          formStore.data.bufferTankId = prevBufferTankId

          bufferTankSalePackage.value = true
        }
      }
      validate()
    }
  },
  { immediate: true }
)

defineExpose({ validate })
</script>

<template>
  <form
    id="buffer-tank-form"
    action=""
    class="steps__form steps-table"
    @submit.prevent=""
  >
    <div class="steps-table__header">
      <div class="header-wrapper">
        <SectionHeadline class="steps-table__title fTable__title" translation-key="step_components.title_buffer_tanks" />

        <div v-if="userStore.enableBufferMinVolumeHint && singleBufferTank && bufferTankIncluded" class="additional-infos">
          <p
            v-sanitize-html="$t('step_components.calculated_necessary_buffer_volume', { volume: minBufferVolume })"
            class="min-buffer-volume" />
          &centerdot;
          <p v-sanitize-html="$t('step_components.min_buffer_flow_rate', { minFlowRate: minFlowRate })" class="min-buffer-flowrate" />
          &centerdot;
          <p class="info-btn">
            {{ $t('step_components.how_is_this_calculated') }}
            <HelpLabel :modal-type="ModalTypes.BUFFER_VOLUME_INFO" />
          </p>
        </div>
      </div>

      <label v-if="userStore.bufferTankOptional || !bufferTankSalePackage" class="form__checkbox steps-table__toggle">
        {{ $t('step_components.include_buffer_tank') }}

        <input
          id="cb-buffertank-included"
          v-model="bufferTankIncluded"
          :disabled="bufferTankSalePackage"
          type="checkbox"
          @change="bufferTankIncludedChanged"
        >
        <span class="form__cm is--large" />
      </label>

      <ProductAvailabilityLink :availability-link="userStore.productAvailabilityLinks?.bufferTanks" />
    </div>

    <HintBox
      v-if="userStore.enableBufferMinVolumeHint && singleBufferTank && !!(formStore.data.bufferTankId || formStore.data.coolingBufferTankId) && uncoveredVolume() > 0"
      :description="$t('step_components.buffer_volume_not_covered', { percent: uncoveredVolumePercentage(minBufferVolume, uncoveredVolume()), missing: uncoveredVolume() })" />

    <div v-airbag="{ targetClass: 'steps__content' }" class="relative">
      <template v-if="bufferTankIncluded">
        <div v-if="projectStore.isCooling && projectStore.isHeating && userStore.enableBufferSeparation" class="card multiple-tables">
          <ButtonSet
            key="button-set-buffer-tank-mode"
            v-model="singleBufferTank"
            :auto-btn-width="true"
            :buttons="[
              {
                caption: $t('step_components.btn_single_buffer_tank'),
                value: true,
              },
              {
                caption: $t('step_components.btn_individual_tank'),
                value: false,
              },
            ]"
            :name="'buffer-tank-mode'"
            @change="individualTankSelectionChanged"
          />

          <div v-if="!singleBufferTank" class="multiple-tables__wrapper">
            <div>
              <div class="icon-text multiple-tables__title">
                <i class="ic-heating" />
                {{ $t('step_components.title_heating_buffer_tank') }}
              </div>

              <div v-if="userStore.enableBufferMinVolumeHint" class="additional-infos">
                <p
                  v-sanitize-html="$t('step_components.calculated_necessary_buffer_volume', { volume: minBufferVolumeHeating })"
                  class="min-buffer-volume" />
                &centerdot;
                <p v-sanitize-html="$t('step_components.min_buffer_flow_rate', { minFlowRate: minFlowRate })" class="min-buffer-flowrate" />
                &centerdot;
                <p class="info-btn">
                  {{ $t('step_components.how_is_this_calculated') }}
                  <HelpLabel :modal-payload="{ installationType: InstallationType.HEATING }" :modal-type="ModalTypes.BUFFER_VOLUME_INFO" />
                </p>
              </div>

              <HintBox
                v-if="userStore.enableBufferMinVolumeHint && !!formStore.data.bufferTankId && uncoveredVolume(InstallationType.HEATING) > 0"
                :description="$t('step_components.buffer_volume_not_covered', { percent: uncoveredVolumePercentage(minBufferVolumeHeating, uncoveredVolume(InstallationType.HEATING)), missing: uncoveredVolume(InstallationType.HEATING) })" />

              <div v-if="!projectStore.isBufferTankSelected()">
                <ProductsSelectTable
                  v-if="heatingBufferTanks?.length"
                  id="heating-buffer-tank-table"
                  key="heating-buffer-tank-table"
                  v-model="formStore.data.bufferTankId"
                  :columns="bufferTankColumns"
                  :products="heatingBufferTanks"
                  :recommendation-type="RecommendationBadgeType.LEFT"
                  :unselectable="false"
                  product-name="buffer-tank"
                />

                <ExclusionHint v-else :title="$t('step_components.no_heating_buffer_available')" />
              </div>

              <ExclusionHint
                v-else
                :text="$t('step_components.buffer_tank_already_included_text')"
                :title="$t('step_components.buffer_tank_already_included')"
              />
            </div>

            <div>
              <div class="icon-text multiple-tables__title">
                <i class="ic-cooling" />
                {{ $t('step_components.title_cooling_buffer_tank') }}
              </div>

              <div v-if="userStore.enableBufferMinVolumeHint" class="additional-infos">
                <p
                  v-sanitize-html="$t('step_components.calculated_necessary_buffer_volume', { volume: minBufferVolumeCooling })"
                  class="min-buffer-volume" />
                &centerdot;
                <p v-sanitize-html="$t('step_components.min_buffer_flow_rate', { minFlowRate: minFlowRate })" class="min-buffer-flowrate" />
                &centerdot;
                <p class="info-btn">
                  {{ $t('step_components.how_is_this_calculated') }}
                  <HelpLabel :modal-payload="{ installationType: InstallationType.COOLING }" :modal-type="ModalTypes.BUFFER_VOLUME_INFO" />
                </p>
              </div>

              <HintBox
                v-if="userStore.enableBufferMinVolumeHint && !!formStore.data.coolingBufferTankId && uncoveredVolume(InstallationType.COOLING) > 0"
                :description="$t('step_components.buffer_volume_not_covered', { percent: uncoveredVolumePercentage(minBufferVolumeCooling, uncoveredVolume(InstallationType.COOLING)), missing: uncoveredVolume(InstallationType.COOLING) })" />

              <div v-if="!projectStore.isCoolingBufferTankSelected()">
                <ProductsSelectTable
                  v-if="coolingBufferTanks?.length"
                  id="cooling-buffer-tank-table"
                  key="cooling-buffer-tank-table"
                  v-model="formStore.data.coolingBufferTankId"
                  :columns="bufferTankColumns"
                  :products="coolingBufferTanks"
                  :recommendation-type="RecommendationBadgeType.LEFT"
                  :unselectable="false"
                  product-name="cooling-buffer-tank"
                />

                <ExclusionHint v-else :title="$t('step_components.no_cooling_buffer_available')" />
              </div>

              <ExclusionHint
                v-else
                :text="$t('step_components.cooling_buffer_tank_already_included_text')"
                :title="$t('step_components.cooling_buffer_tank_already_included')"
              />
            </div>
          </div>
          <div v-else class="multiple-tables__wrapper">
            <div v-if="!projectStore.isCoolingBufferTankSelected()">
              <ProductsSelectTable
                v-if="coolingBufferTanks?.length"
                id="single-buffer-tank-table"
                key="single-buffer-tank-table"
                v-model="formStore.data.bufferTankId"
                :columns="bufferTankColumns"
                :products="coolingBufferTanks"
                :recommendation-type="RecommendationBadgeType.LEFT"
                :unselectable="false"
                product-name="buffer-tank"
              />

              <div v-else>
                {{ $t('step_components.no_cooling_buffer_available') }}
              </div>
            </div>

            <ExclusionHint
              v-else
              :text="$t('step_components.cooling_buffer_tank_already_included_text')"
              :title="$t('step_components.cooling_buffer_tank_already_included')"
            />
          </div>
        </div>

        <div v-else class="multiple-tables__wrapper">
          <!-- Buffertank for both (buffertankId)-->
          <div v-if="!projectStore.isBufferTankSelected() && projectStore.isHeating && projectStore.isCooling">
            <ProductsSelectTable
              v-if="combinedBufferTanks?.length"
              key="buffer-tank-table"
              v-model="formStore.data.bufferTankId"
              :columns="bufferTankColumns"
              :products="combinedBufferTanks"
              :recommendation-type="RecommendationBadgeType.LEFT"
              :unselectable="false"
              product-name="buffer-tank"
            />

            <ExclusionHint v-else :alert="true" :title="$t('step_components.no_buffer_available')" />
          </div>

          <div v-if="!projectStore.isBufferTankSelected() && projectStore.isHeating && !projectStore.isCooling">
            <ProductsSelectTable
              v-if="heatingBufferTanks?.length"
              key="buffer-tank-table"
              v-model="formStore.data.bufferTankId"
              :columns="bufferTankColumns"
              :products="heatingBufferTanks"
              :recommendation-type="RecommendationBadgeType.LEFT"
              :unselectable="false"
              product-name="buffer-tank"
            />

            <ExclusionHint v-else :title="$t('step_components.no_heating_buffer_available')" />
          </div>
          <div v-else-if="!projectStore.isBufferTankSelected() && !projectStore.isHeating && projectStore.isCooling">
            <ProductsSelectTable
              v-if="coolingBufferTanks?.length"
              id="single-buffer-tank-table"
              key="single-buffer-tank-table"
              v-model="formStore.data.coolingBufferTankId"
              :columns="bufferTankColumns"
              :products="coolingBufferTanks"
              :recommendation-type="RecommendationBadgeType.LEFT"
              :unselectable="false"
              product-name="buffer-tank"
            />

            <ExclusionHint v-else :title="$t('step_components.no_cooling_buffer_available')" />
          </div>

          <ExclusionHint
            v-else-if="projectStore.isBufferTankSelected()"
            :text="$t('step_components.buffer_tank_already_included_info')"
            :title="$t('step_components.buffer_tank_already_included')"
          />
        </div>
      </template>

      <ExclusionHint
        v-if="!bufferTankIncluded && userStore.enableBufferMinVolumeHint"
        :alert="true"
        :text="$t('step_components.buffer_tank_excluded_min_volume_description', { volume: minBufferVolume })"
        :title="$t('step_components.buffer_tank_excluded_title')"
      />

      <ExclusionHint
        v-else-if="!bufferTankIncluded"
        :text="$t('step_components.buffer_tank_excluded_description')"
        :title="$t('step_components.buffer_tank_excluded_title')"
      />
    </div>
  </form>
</template>

<style lang="scss" scoped>
@import "@/assets/style/helpers/include-media";

.steps-table__header {
  align-items: flex-start;

  .header-wrapper {
    margin-right: auto;
  }
}

.additional-infos {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;

  @include media('<tablet') {
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 0;
    display: flex;
    gap: 0.25rem;
    color: $defaultPrimaryColor;
    align-items: center;
  }

  .btn.is--raw {
    padding: 0;
  }
}

.hint-box {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.multiple-tables {
  padding: rem-calc(32);

  &__title {
    margin-bottom: rem-calc(14);

    i[class^="ic-"],
    i[class*=" ic-"],
    i.ic {
      &:before {
        font-size: rem-calc(32);

        @include themify($themes) {
          color: themed("primaryColor");
        }
      }
    }
  }

  .multiple-tables__wrapper > div {
    &:last-child {
      .fTable {
        margin-bottom: 0;
      }
    }
  }

  .fTable {
    overflow: hidden;
    margin-bottom: rem-calc(27);
    border: 1px solid $tableHeaderBorderColor;

    @include themify($themes) {
      border-radius: rem-calc(themed("cardBorderRadius"));
    }
  }
}
</style>
